import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, Spinner, Input, Textarea, RichSelect,
} from './shared';

const TYPE_BADGES = {
    bonus: 'success', multiplier: 'purple', override: 'warning',
};

const TYPE_LABELS = {
    bonus: '+$', multiplier: 'x', override: '=$',
};

const SOURCE_BADGES = {
    admin: 'info', promotion: 'purple', compensation: 'warning', referral: 'success',
};

const ADJUSTMENT_TYPE_OPTIONS = [
    { value: 'bonus', label: 'Bonus (+$)', desc: 'Cộng thêm quota vào giới hạn hiện tại' },
    { value: 'multiplier', label: 'Multiplier (x)', desc: 'Nhân gấp quota (2.0 = gấp đôi)' },
    { value: 'override', label: 'Override (=$)', desc: 'Ghi đè cố định, bỏ qua base limit' },
];

const WINDOW_TYPE_OPTIONS = [
    { value: '', label: 'Tất cả windows', desc: 'Áp dụng cho mọi window (weekly, monthly, ...)' },
    { value: 'weekly', label: 'Weekly', desc: 'Chỉ áp dụng giới hạn tuần' },
    { value: 'monthly', label: 'Monthly', desc: 'Chỉ áp dụng giới hạn tháng' },
    { value: 'daily', label: 'Daily', desc: 'Chỉ áp dụng giới hạn ngày' },
    { value: 'trial', label: 'Trial', desc: 'Chỉ áp dụng giới hạn dùng thử' },
];

const SOURCE_OPTIONS = [
    { value: 'admin', label: 'Admin', desc: 'Admin chủ động' },
    { value: 'promotion', label: 'Promotion', desc: 'Chương trình khuyến mãi' },
    { value: 'compensation', label: 'Compensation', desc: 'Bồi thường' },
    { value: 'referral', label: 'Referral', desc: 'Thưởng giới thiệu' },
];

const SCOPE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'global', label: 'Global only' },
    { value: 'user', label: 'User only' },
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];


function DayPicker({ selected, onToggle }) {
    return (
        <div className="flex gap-1 flex-wrap">
            {DAY_LABELS.map((label, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={() => onToggle(i)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${selected.includes(i) ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function RuleEditor({ rule, onUpdate, onRemove }) {
    const toggleDay = (d) => {
        const days = rule.days || [];
        const next = days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort();
        onUpdate({ ...rule, days: next });
    };

    const addHour = () => {
        const hours = rule.hours || [];
        onUpdate({ ...rule, hours: [...hours, ['08:00', '17:00']] });
    };

    const updateHour = (idx, pos, val) => {
        const hours = (rule.hours || []).map((h, i) =>
            i === idx ? (pos === 0 ? [val, h[1]] : [h[0], val]) : h
        );
        onUpdate({ ...rule, hours });
    };

    const removeHour = (idx) => {
        const hours = (rule.hours || []).filter((_, i) => i !== idx);
        onUpdate({ ...rule, hours });
    };

    return (
        <div className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Ngày áp dụng</span>
                <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600 text-xs">Xóa rule</button>
            </div>
            <DayPicker selected={rule.days || []} onToggle={toggleDay} />
            <div className="space-y-1.5">
                <span className="text-xs font-medium text-gray-500">Khung giờ (trống = cả ngày)</span>
                {(rule.hours || []).map((range, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                        <input type="time" value={range[0]} onChange={(e) => updateHour(idx, 0, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1" />
                        <span className="text-xs text-gray-400">→</span>
                        <input type="time" value={range[1]} onChange={(e) => updateHour(idx, 1, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1" />
                        <button type="button" onClick={() => removeHour(idx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                    </div>
                ))}
                <button type="button" onClick={addHour} className="text-xs text-blue-600 hover:text-blue-800">+ Thêm khung giờ</button>
            </div>
        </div>
    );
}

function ScheduleBuilder({ value, onChange }) {
    const detectMode = (v) => {
        if (!v) return 'always';
        if (v.rules) return 'rules';
        if (v.days) return 'days';
        return 'always';
    };

    const [mode, setMode] = useState(() => detectMode(value));
    const [days, setDays] = useState(() => value?.days || []);
    const [rules, setRules] = useState(() => value?.rules || []);

    useEffect(() => {
        setMode(detectMode(value));
        setDays(value?.days || []);
        setRules(value?.rules || []);
    }, [JSON.stringify(value)]);

    const switchMode = (m) => {
        setMode(m);
        if (m === 'always') onChange(null);
        else if (m === 'days') onChange(days.length > 0 ? { days } : null);
        else if (m === 'rules') onChange(rules.length > 0 ? { rules } : null);
    };

    const toggleDay = (d) => {
        const next = days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort();
        setDays(next);
        onChange(next.length > 0 ? { days: next } : null);
    };

    const emitRules = (updated) => {
        const valid = updated.filter(r => r.days?.length > 0 || r.hours?.length > 0);
        onChange(valid.length > 0 ? { rules: valid } : null);
    };

    const updateRule = (idx, rule) => {
        const next = rules.map((r, i) => i === idx ? rule : r);
        setRules(next);
        emitRules(next);
    };

    const removeRule = (idx) => {
        const next = rules.filter((_, i) => i !== idx);
        setRules(next);
        emitRules(next);
    };

    const addRule = () => {
        const next = [...rules, { days: [], hours: [] }];
        setRules(next);
        emitRules(next);
    };

    return (
        <div className="space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex gap-2 text-xs">
                {[['always', 'Luôn áp dụng'], ['days', 'Theo ngày'], ['rules', 'Ngày + giờ']].map(([m, label]) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => switchMode(m)}
                        className={`px-2.5 py-1 rounded-md transition-colors ${mode === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {mode === 'days' && (
                <DayPicker selected={days} onToggle={toggleDay} />
            )}

            {mode === 'rules' && (
                <div className="space-y-3">
                    {rules.map((rule, idx) => (
                        <RuleEditor
                            key={idx}
                            rule={rule}
                            onUpdate={(r) => updateRule(idx, r)}
                            onRemove={() => removeRule(idx)}
                        />
                    ))}
                    <button type="button" onClick={addRule} className="text-xs text-blue-600 hover:text-blue-800">+ Thêm rule</button>
                </div>
            )}
        </div>
    );
}


function formatSchedule(schedule) {
    if (!schedule) return 'Always';
    const dayStr = (days) => days?.map(d => DAY_LABELS[d]).join(', ') || 'Every day';
    if (schedule.days) return dayStr(schedule.days);
    if (schedule.rules) {
        return schedule.rules.map(r => {
            const d = dayStr(r.days);
            const h = r.hours?.map(([s, e]) => `${s}-${e}`).join(', ') || 'all day';
            return `${d} ${h}`;
        }).join(' | ');
    }
    return 'Always';
}


export default function AdminBudgetAdjustments() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterUserId, setFilterUserId] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [filterScope, setFilterScope] = useState('');
    const [page, setPage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tierOptions, setTierOptions] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { loadItems(); }, [filterUserId, filterActive, filterScope, page]);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiClient.listTierWindowLimits(null, true);
                const names = [...new Set((res.items || []).map(i => i.tier_name))];
                setTierOptions([
                    { value: '', label: 'All tiers', desc: 'Áp dụng cho tất cả tier' },
                    ...names.map(n => ({ value: n, label: n })),
                ]);
            } catch {}
        })();
    }, []);

    const loadItems = async () => {
        try {
            setLoading(true);
            const isActive = filterActive === '' ? undefined : filterActive === 'true';
            const res = await apiClient.listBudgetAdjustments({
                userId: filterUserId || undefined,
                isActive,
                scope: filterScope || undefined,
                page,
                size: 50,
            });
            setItems(res.items || []);
            setTotal(res.total || 0);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    const fmtLocal = (iso) => {
        if (!iso) return '';
        try { return new Date(iso).toISOString().slice(0, 16); } catch { return ''; }
    };

    const handleCreate = () => {
        setForm({
            _editId: null,
            user_id: filterUserId || '',
            adjustment_type: 'bonus',
            window_type: '',
            amount: '',
            tier_filter: '',
            schedule: null,
            source: 'admin',
            effective_from: '',
            effective_until: '',
            is_active: true,
            note: '',
        });
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setForm({
            _editId: item.id,
            user_id: item.user_id || '',
            adjustment_type: item.adjustment_type,
            window_type: item.window_type || '',
            amount: String(item.amount),
            tier_filter: item.tier_filter || '',
            schedule: item.schedule,
            source: item.source,
            effective_from: fmtLocal(item.effective_from),
            effective_until: fmtLocal(item.effective_until),
            is_active: item.is_active,
            note: item.note || '',
        });
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            if (form._editId) {
                await apiClient.updateBudgetAdjustment(form._editId, {
                    user_id: form.user_id || null,
                    adjustment_type: form.adjustment_type,
                    window_type: form.window_type || null,
                    amount: parseFloat(form.amount),
                    tier_filter: form.tier_filter || null,
                    schedule: form.schedule,
                    source: form.source,
                    effective_from: form.effective_from || null,
                    effective_until: form.effective_until || null,
                    is_active: form.is_active,
                    note: form.note || null,
                });
                setSuccessMessage(`Updated adjustment #${form._editId}`);
            } else {
                const payload = {
                    user_id: form.user_id || null,
                    adjustment_type: form.adjustment_type,
                    window_type: form.window_type || null,
                    amount: parseFloat(form.amount),
                    tier_filter: form.tier_filter || null,
                    schedule: form.schedule,
                    source: form.source,
                    effective_from: form.effective_from || null,
                    effective_until: form.effective_until || null,
                    note: form.note || null,
                };
                await apiClient.createBudgetAdjustment(payload);
                const scope = payload.user_id ? payload.user_id : 'ALL users';
                const label = TYPE_LABELS[payload.adjustment_type] || '+$';
                setSuccessMessage(`Created ${label}${payload.amount} for ${scope}`);
            }
            setIsModalOpen(false);
            loadItems();
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const isEditing = !!form?._editId;

    const handleToggleActive = async (item) => {
        try {
            await apiClient.updateBudgetAdjustment(item.id, { is_active: !item.is_active });
            setSuccessMessage(`${item.is_active ? 'Deactivated' : 'Activated'} adjustment #${item.id}`);
            loadItems();
        } catch (err) {
            setError(err.message || 'Update failed');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this adjustment?')) return;
        try {
            await apiClient.deleteBudgetAdjustment(id);
            setSuccessMessage('Deleted');
            loadItems();
        } catch (err) {
            setError(err.message || 'Delete failed');
        }
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';
    const fmtAmount = (item) => `${TYPE_LABELS[item.adjustment_type] || ''}${item.amount}`;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Budget Adjustments</h2>
                <Button onClick={handleCreate}>+ Add Adjustment</Button>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert type="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            <div className="flex gap-3 items-center flex-wrap">
                <input
                    type="text"
                    value={filterUserId}
                    onChange={(e) => { setFilterUserId(e.target.value); setPage(0); }}
                    placeholder="Filter by user ID"
                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48"
                />
                <select
                    value={filterActive}
                    onChange={(e) => { setFilterActive(e.target.value); setPage(0); }}
                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                >
                    <option value="">All status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
                <select
                    value={filterScope}
                    onChange={(e) => { setFilterScope(e.target.value); setPage(0); }}
                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                >
                    {SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span className="text-xs text-gray-400">{total} results</span>
            </div>

            {loading ? <Spinner /> : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-left text-gray-500">
                                    <th className="px-4 py-2">Scope</th>
                                    <th className="px-4 py-2">Type</th>
                                    <th className="px-4 py-2">Amount</th>
                                    <th className="px-4 py-2">Window</th>
                                    <th className="px-4 py-2">Schedule</th>
                                    <th className="px-4 py-2">Source</th>
                                    <th className="px-4 py-2">Start</th>
                                    <th className="px-4 py-2">Expires</th>
                                    <th className="px-4 py-2">Active</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                            {item.is_global ? (
                                                <Badge variant="purple">Global</Badge>
                                            ) : (
                                                <span className="font-mono text-xs">{item.user_id?.slice(0, 12)}...</span>
                                            )}
                                            {item.tier_filter && (
                                                <span className="text-xs text-gray-400 ml-1">({item.tier_filter})</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <Badge variant={TYPE_BADGES[item.adjustment_type] || 'default'}>{item.adjustment_type}</Badge>
                                        </td>
                                        <td className="px-4 py-2 font-semibold text-green-600">{fmtAmount(item)}</td>
                                        <td className="px-4 py-2 text-gray-500">{item.window_type || 'all'}</td>
                                        <td className="px-4 py-2 text-gray-500 text-xs max-w-[150px] truncate" title={formatSchedule(item.schedule)}>
                                            {formatSchedule(item.schedule)}
                                        </td>
                                        <td className="px-4 py-2">
                                            <Badge variant={SOURCE_BADGES[item.source] || 'default'}>{item.source}</Badge>
                                        </td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(item.effective_from)}</td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(item.effective_until)}</td>
                                        <td className="px-4 py-2">
                                            <button onClick={() => handleToggleActive(item)} title={item.is_active ? 'Deactivate' : 'Activate'}>
                                                <span className={`w-2 h-2 rounded-full inline-block ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-2 flex gap-2">
                                            <button onClick={() => handleEdit(item)} className="text-blue-500 hover:text-blue-700 text-xs">Edit</button>
                                            <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No adjustments</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {total > 50 && (
                        <div className="flex justify-center gap-2 p-3 border-t border-gray-100">
                            <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
                            <span className="text-sm text-gray-500 py-1">Page {page + 1}</span>
                            <Button variant="secondary" size="sm" disabled={items.length < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
                        </div>
                    )}
                </Card>
            )}

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} title={form?._editId ? `Edit Adjustment #${form._editId}` : 'Add Budget Adjustment'} size="lg">
                {form && (
                    <div className="p-6 space-y-4">
                        {isEditing && (
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                                <span className="text-sm text-gray-600">Active</span>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                                    className={`relative w-10 h-5 rounded-full transition-colors ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
                                </button>
                            </div>
                        )}
                        <Input
                            label="User ID (empty = global, all users)"
                            value={form.user_id}
                            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                            placeholder="Leave empty for global adjustment"

                        />
                        <RichSelect
                            label="Adjustment Type"
                            value={form.adjustment_type}
                            onChange={(v) => setForm({ ...form, adjustment_type: v })}
                            options={ADJUSTMENT_TYPE_OPTIONS}

                        />
                        <Input
                            label={form.adjustment_type === 'multiplier' ? 'Multiplier (e.g. 2 = x2)' : form.adjustment_type === 'override' ? 'Override amount ($)' : 'Bonus amount ($)'}
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            placeholder={form.adjustment_type === 'multiplier' ? '2.0' : '1.00'}
                        />
                        <RichSelect
                            label="Window Type"
                            value={form.window_type}
                            onChange={(v) => setForm({ ...form, window_type: v })}
                            options={WINDOW_TYPE_OPTIONS}
                            placeholder="-- All windows --"

                        />
                        <RichSelect
                            label="Tier Filter"
                            value={form.tier_filter}
                            onChange={(v) => setForm({ ...form, tier_filter: v })}
                            options={tierOptions}
                            placeholder="-- All tiers --"

                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                            <ScheduleBuilder value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} />
                        </div>
                        <RichSelect
                            label="Source"
                            value={form.source}
                            onChange={(v) => setForm({ ...form, source: v })}
                            options={SOURCE_OPTIONS}

                        />
                        <Input
                            label="Start time (empty = now)"
                            type="datetime-local"
                            value={form.effective_from}
                            onChange={(e) => setForm({ ...form, effective_from: e.target.value })}

                        />
                        <Input
                            label="Expires (empty = no expiry)"
                            type="datetime-local"
                            value={form.effective_until}
                            onChange={(e) => setForm({ ...form, effective_until: e.target.value })}
                        />
                        <Textarea
                            label="Note"
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            rows={2}
                            placeholder="Reason for adjustment"
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving || !form.amount}>
                                {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
